import DynamicForm from "@/common/DynamicForm";
import { StyledGridRow } from "@/common/styles";
import { BookingsSectionTitle } from "@/components/BookingsDetails/styles";
import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import {
  convertApiFields,
  isNonEmptyVariable,
  getSQLTypeToHTMLType,
  debounce,
  hasCheckSecurity,
} from "@/util/common_function";
import dayjs from "dayjs";

// StaffExtra's own PK/FK columns, lower-first — mirrors
// AdditionalInmateMedicalInfo.jsx's INMATE_MEDICAL_EXTRA_ID_KEY constants.
const STAFF_EXTRA_ID_KEY = "staffExtraID";
const STAFF_ID_COLUMN = "staffID";

// "Additional Staff Information" — PCI's StaffInterface.asp renders this as
// a supplemental section right before "Staff Administrative Information"
// (see StaffAdministrativeInformation.jsx, which renders this component
// right above its own content to match that order).
const AdditionalStaffInformation = ({ startingPoint = "" }) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );
  const [form] = Form.useForm();
  const [apiFields, setApiFields] = useState([]);
  const [additionalStaffInfo, setAdditionalStaffInfo] = useState(null);
  // True once fieldHtmlTypeMapRef is populated — gates the effect below so a
  // date/datetime column's value is never handed to the form as a plain
  // string (see AdditionalPersonalInformation.jsx for why that matters).
  const [fieldsReady, setFieldsReady] = useState(false);

  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  // columnName -> htmlType ("date" / "datetime" / ...) for every StaffExtra
  // field.
  const fieldHtmlTypeMapRef = useRef({});

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/StaffExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U"),
        );
        const converted = convertApiFields(tableData, "StaffExtra");

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        fieldHtmlTypeMapRef.current = typeMap;

        setApiFields(converted);
        setFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[AdditionalStaffInformation] StaffExtra field defs fetch failed:",
          error,
        );
      });
  }, []);

  // StaffExtra is a normal onetoone entity on the staff record (see
  // RecordStructureStaff.asp's AddLink), so its base row is expected to
  // come back nested under bookings.staffExtra[0] from the ordinary staff
  // GET/save cycle — no separate resolve call for the parent id, same
  // reasoning as AdditionalInmateMedicalInfo.jsx.
  useEffect(() => {
    if (!fieldsReady) return;

    if (isNonEmptyVariable(bookings)) {
      const info = bookings?.staffExtra?.[0];
      const withDates = info
        ? Object.keys(info).reduce((acc, key) => {
            const val = info[key];
            const htmlType = fieldHtmlTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] =
              isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {})
        : info;
      setAdditionalStaffInfo(withDates);
    } else {
      setAdditionalStaffInfo(null);
    }
  }, [bookings, fieldsReady]);

  useEffect(() => {
    if (isNonEmptyVariable(additionalStaffInfo)) {
      form.setFieldsValue(additionalStaffInfo);
    } else {
      form.resetFields();
    }
  }, [additionalStaffInfo]);

  // Merge current visible form values into bookings.staffExtra[0], then
  // dispatch. Reads bookingsRef (not the `bookings` component-scope
  // variable) since this is called from a debounced callback created once
  // (see dataModifyRef below) — the ref always holds the latest redux
  // value, avoiding a stale closure.
  const updateForm = (currentValues) => {
    const staff = bookingsRef.current || {};
    const existingExtra = staff?.staffExtra?.[0] || {};

    const formattedValues = Object.keys(currentValues).reduce((acc, key) => {
      const val = currentValues[key];
      const htmlType = fieldHtmlTypeMapRef.current[key];

      if (val) {
        if (htmlType === "date") {
          acc[key] = dayjs(val).format("MM/DD/YYYY");
        } else if (htmlType === "datetime") {
          acc[key] = dayjs(val).format("MM/DD/YYYY HH:mm");
        } else {
          acc[key] = val;
        }
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    dispatch(
      setBookings({
        staffExtra: [
          {
            ...existingExtra,
            ...formattedValues,
            [STAFF_EXTRA_ID_KEY]: existingExtra?.[STAFF_EXTRA_ID_KEY] ?? 0,
            [STAFF_ID_COLUMN]:
              existingExtra?.[STAFF_ID_COLUMN] ?? staff?.staffId ?? 0,
          },
        ],
      }),
    );
  };

  // Debounced so rapid keystrokes/onFieldsChange events don't dispatch on
  // every single one — same pattern as AdditionalInmateMedicalInfo.jsx's
  // dataModifyRef.
  const dataModifyRef = useRef(null);
  if (!dataModifyRef.current) {
    dataModifyRef.current = debounce(() => {
      updateForm(form.getFieldsValue());
    }, 800);
  }

  if (!apiFields.length) return null;

  return (
    <>
      <BookingsSectionTitle style={{ marginTop: "0" }}>
        <span>Additional Staff Information</span>
      </BookingsSectionTitle>
      <Form
        form={form}
        onFieldsChange={() => {
          dataModifyRef.current();
        }}
        disabled={!bookingsFieldsEnabled}
      >
        <StyledGridRow gutter={[16]}>
          {apiFields.map((sf, index) => {
            const view = hasCheckSecurity(
              "Functionality", "field", "VIEW", "", "",
              sf.tableName, sf.columnName, startingPoint,
            );
            const edit = hasCheckSecurity(
              "Functionality", "field", "MODIFY", "", "",
              sf.tableName, sf.columnName, startingPoint,
            );
            return (
              <DynamicForm
                key={index}
                formAttributes={sf}
                form={form}
                bookingsFieldsEnabled={bookingsFieldsEnabled}
                view={view}
                edit={edit}
              />
            );
          })}
        </StyledGridRow>
      </Form>
    </>
  );
};

export default AdditionalStaffInformation;
